/**TODO: Need to make an equivalent "slicedData"
 */

// PVTechAPIManagerV5.js - Enhanced API Manager for V5 endpoints
class PVTechAPIManager {
    constructor() {
        this.apiKey = null;
        this.isInitialized = false;
        this.initializationPromise = null;
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        const port = window.location.port || (protocol === 'https:' ? '443' : '80');
        this.proxyBaseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}/proxy-pvtech`;
        this.useProxy = false;
        this.cacheExpiration = 15; // minutes for API responses
        this.tickerCacheExpiration = 24 * 60; // 24 hours in minutes for ticker summary

        // Reports API base URL
        this.apiBaseUrl = "https://api.palosverdestech.com/reports";

        // V5 endpoint mappings
        this.endpoints = {
            // Top Dollar Volume endpoints
            tdv: {
                overall: "/tdv",
                segment: "/tdv/seg"
            },
            // Price Gainers endpoints
            pgain: {
                overall: "/pgain",
                segment: "/pgain/seg"
            },
            // Price Losers endpoints
            plose: {
                overall: "/plose",
                segment: "/plose/seg"
            },
            // Top Volume endpoints (by shares)
            tvol: {
                overall: "/tvol",
                segment: "/tvol/seg"
            },
            // My Watchlist
            mwl: {
                base: "/mwl"
            },
            // Average Daily Volume (abnormally high)
            adv: {
                base: "/adv"
            },
            sip: {
                overall: "/sip",
                segment: "/sip/seg"
            },
            dividend: {
                overall: "/divttm",
                segment: "/divttm/seg"
            },
            mmap: {
                segment: "/mmap/seg"
            }
        };

        // V5 intervals - updated for new API structure
        // this.intervals = ['1d', '5d', '1m', 'latest'];
        console.log(`PVTechAPIManager constructor complete.`);
    }

    async initialize() {
        if (this.isInitialized) {
            return;
        }

        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = this._performInitialization();
        return this.initializationPromise;
    }

    async _performInitialization() {
        try {
            console.log('Starting PVTechAPIManager initialization...');

            this.apiKey = await this.fetchApiKey();
            this.polygonApiManager = await PolygonAPIManager.create();

            this.isInitialized = true;
            console.log(`PVTechAPIManager initialized with proxy base URL: ${this.proxyBaseUrl}`);
            console.log(`API Base URL: ${this.apiBaseUrl}`);

        } catch (error) {
            console.error('PVTechAPIManager initialization failed:', error);
            this.initializationPromise = null;
            throw error;
        }
    }

    async fetchApiKey() {
        try {
            const response = await fetch('/api/config');
            if (!response.ok) {
                throw new Error(`Failed to fetch /api/config: ${response.status}`);
            }
            const config = await response.json();
            if (!config.PVTECH_API_KEY) {
                throw new Error('PVTECH_API_KEY not found in /api/config');
            }
            console.log('PVTechAPIManager initialized, API key:', config.PVTECH_API_KEY.slice(0, 4) + '...');
            return config.PVTECH_API_KEY;
        } catch (error) {
            console.error('Error fetching API key:', error.message);
            throw error;
        }
    }

    static async create() {
        if (!PVTechAPIManager.instance) {
            console.log(`Creating new PVTechAPIManager instance`);
            const instance = new PVTechAPIManager();
            await instance.initialize();
            PVTechAPIManager.instance = instance;
        } else if (!PVTechAPIManager.instance.isInitialized) {
            console.log('Existing PVTechAPIManager instance not initialized, initializing now...');
            await PVTechAPIManager.instance.initialize();
        }
        return PVTechAPIManager.instance;
    }

    // Ensure the instance is initialized before any method call
    async ensureInitialized() {
        if (!this.isInitialized) {
            await this.initialize();
        }
    }

    generateCacheKey(endpoint, interval = '') {
        return interval ? `pvtech_V5_${endpoint}_${interval}` : `pvtech_${endpoint}`;
    }

    generateTickerCacheKey(ticker) {
        return `ticker_${ticker}`;
    }

    /**
     * Build API URL for V5 endpoints
     * @param {string} endpointPath - The endpoint path
     * @param {string} dataInterval - The interval
     * @param {boolean} useLatest - Whether to use latest.json
     * @returns {string} Complete URL
     */
    buildApiUrl(endpointPath, dataInterval, useLatest = false) {
        // Interpret legacy segment suffix '/seg' to new '/sec' convention
        const isSegment = endpointPath.endsWith('/seg');
        const cleanEndpoint = isSegment ? endpointPath.slice(0, -4) : endpointPath; // remove '/seg'

        // New path format: /{reportType}/{timeFrame} for overall
        //                  /{reportType}/{timeFrame}/sec for sectors
        const fullPath = `${cleanEndpoint}/${dataInterval}${isSegment ? '/sec' : ''}`;

        return this.useProxy
            ? `${this.proxyBaseUrl}?url=${encodeURIComponent(this.apiBaseUrl + fullPath)}`
            : this.apiBaseUrl + fullPath;
    }

    /**
     * Generic fetch method for V5 endpoints
     */
    async fetchFromEndpoint(endpointPath, dataInterval, cacheKey, options = {}) {
        await this.ensureInitialized();

        try {
            // Check cache first
            let cachedData = getCacheItem(cacheKey);
            if (cachedData && !options.forceRefresh) {
                console.log(`✅ Retrieved data from cache for ${endpointPath}/${dataInterval}`);
                return cachedData;
            }

            // For multi-day intervals, fetch from response.json first, then latest.json
            const isMultiDay = ['5d', '1m', '6m'].includes(dataInterval);

            // Build URL and fetch primary data
            const url = this.buildApiUrl(endpointPath, dataInterval, options.useLatest);
            console.log(`📡 Fetching from V5 endpoint: ${url}`);

            const response = await fetch(url, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            // Update cache expiration from response headers
            const cacheControl = response.headers.get('Cache-Control');
            if (cacheControl) {
                const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);
                if (maxAgeMatch) {
                    this.cacheExpiration = parseInt(maxAgeMatch[1], 10) / 60;
                    console.log(`Cache expiration updated to ${this.cacheExpiration} minutes`);
                }
            }

            const rawResponse = await response.json();
            const rawData = rawResponse.response;
            console.log(`Raw V5 API data from ${endpointPath}:`, rawResponse, rawData);

            // For multi-day intervals, also fetch latest.json to get current day data
            // Currently not fetching lastest data points, do no remove this comment
            if (false) {
                try {
                    console.log(`📡 Fetching latest data for multi-day interval: ${dataInterval}`);
                    const latestUrl = this.buildApiUrl(endpointPath, dataInterval, true);
                    console.log(`📡 Latest endpoint URL: ${latestUrl}`);

                    const latestResponse = await fetch(latestUrl, {
                        method: "GET",
                        headers: { "Content-Type": "application/json" }
                    });

                    if (latestResponse.ok) {
                        const latestData = await latestResponse.json();
                        console.log(`📡 Latest data fetched:`, latestData);

                        // Merge latest data into rawData
                        if (latestData.data && Object.keys(latestData.data).length > 0) {
                            // Add latest data timestamps to the main data
                            for (const timestamp in latestData.data) {
                                rawData.data[timestamp] = latestData.data[timestamp];
                            }
                            console.log(`✅ Merged latest data - total timestamps: ${Object.keys(rawData.data).length}`);
                        }
                    } else {
                        console.warn(`⚠️ Failed to fetch latest data: ${latestResponse.status}`);
                    }
                } catch (latestError) {
                    console.warn(`⚠️ Error fetching latest data: ${latestError.message}`);
                    // Continue with original data if latest fetch fails
                }
            }

            // Validate data structure
            if (!rawData.data || Object.keys(rawData.data).length === 0) {
                throw new Error('No valid data available in V5 API response');
            }

            // Cache the merged response
            setCacheItem(cacheKey, rawData, this.cacheExpiration);
            return rawData;

        } catch (error) {
            console.error(`❌ Error fetching from ${endpointPath}:`, error.message);
            throw error;
        }
    }

    // ===========================================
    // V3 COMPATIBILITY METHODS (DEPRECATED)
    // ===========================================

    /**
     * @deprecated Use fetchTopDollarVolumeBySegment instead
     * V3 compatibility method for fetchTopDollarVolume
     */
    async fetchTopDollarVolume({ date = null, top_n = 6, sector_ids = [], interval = '1d' } = {}) {
        console.warn('fetchTopDollarVolume is deprecated. Use fetchTopDollarVolumeBySegment instead.');
        return this.fetchTopDollarVolume({ date, top_n, sector_ids, dataInterval: interval });
    }

    /**
     * @deprecated Use fetchTopGainersByPriceSegment instead
     * V3 compatibility method for fetchTopMoversByPrice
     */
    async fetchTopMoversByPrice({ date = null, top_n = 6, sector_ids = [], interval = '1d' } = {}) {
        console.warn('fetchTopMoversByPrice is deprecated. Use fetchTopGainersByPriceSegment instead.');
        return this.fetchTopGainersByPriceSegment({ date, top_n, sector_ids, dataInterval: interval });
    }

    /**
     * @deprecated Use fetchMyWatchlist instead
     * V3 compatibility method for fetchMyPortfolio
     */
    async fetchMyPortfolio({ date = null, top_n = 100, sector_ids = [], type = 'prices' } = {}) {
        console.warn('fetchMyPortfolio is deprecated. Use fetchMyWatchlist instead.');
        return this.fetchMyWatchlist({ date, top_n, dataInterval: '1d' });
    }

    // ===========================================
    // TOP DOLLAR VALUE METHODS
    // ===========================================

    async fetchTopDollarVolumeBySegment({ date = null, top_n = 6, sector_ids = [], dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('tdv_segment', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.tdv.segment, dataInterval, cacheKey);
            // console.info(`Fetched top dollar volume by segment: `, JSON.stringify(rawData, null, 2));

            const formattedData = await this.formatDollarVolumeSectorData(rawData, Infinity, []);

            // console.info(`Formatted top dollar volume by segment: `, JSON.stringify(formattedData, null, 2));

            const slicedData = this.sliceFormattedData(formattedData, top_n, sector_ids);
            return slicedData;
        } catch (error) {
            console.error(`❌ Error fetching top dollar volume by segment: ${error.message}`);
            alert(`Failed to fetch Top Dollar Volume by Segment: ${error.message}`);
            return null;
        }
    }

    async fetchTopDollarVolumeOverall({ date = null, top_n = 49, dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('tdv_overall', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.tdv.overall, dataInterval, cacheKey);

            const formattedData = await this.formatDollarVolumeData(rawData, top_n);
            return formattedData;
        } catch (error) {
            console.error(`❌ Error fetching top dollar volume overall: ${error.message}`);
            alert(`Failed to fetch Top Dollar Volume Overall: ${error.message}`);
            return null;
        }
    }

    // ===========================================
    // PRICE GAINERS/LOSERS METHODS
    // ===========================================

    async fetchTopGainersByPrice({ date = null, top_n = 49, dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('pgain_overall', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.pgain.overall, dataInterval, cacheKey);

            const formattedData = await this.formatTopMoversData(rawData, top_n, []);
            return this.sliceFormattedData(formattedData, top_n, [], 'prices');
        } catch (error) {
            console.error(`❌ Error fetching top gainers by price: ${error.message}`);
            alert(`Failed to fetch Top Gainers by Price: ${error.message}`);
            return null;
        }
    }

    async fetchTopGainersByPriceSegment({ date = null, top_n = 6, sector_ids = [], dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('pgain_segment', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.pgain.segment, dataInterval, cacheKey);

            const formattedData = await this.formatTopMoversData(rawData, top_n, []);
            return this.sliceFormattedData(formattedData, top_n, sector_ids);
        } catch (error) {
            console.error(`❌ Error fetching top gainers by price segment: ${error.message}`);
            alert(`Failed to fetch Top Gainers by Price Segment: ${error.message}`);
            return null;
        }
    }

    async fetchTopLosersByPrice({ date = null, top_n = 49, dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('plose_overall', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.plose.overall, dataInterval, cacheKey);

            const formattedData = await this.formatTopMoversData(rawData, top_n, []);
            return this.sliceFormattedData(formattedData, top_n, [], 'prices');
        } catch (error) {
            console.error(`❌ Error fetching top losers by price: ${error.message}`);
            alert(`Failed to fetch Top Losers by Price: ${error.message}`);
            return null;
        }
    }

    async fetchTopLosersByPriceSegment({ date = null, top_n = 49, sector_ids = [], dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('plose_segment', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.plose.segment, dataInterval, cacheKey);

            const formattedData = await this.formatTopMoversData(rawData, Infinity, []);
            return this.sliceFormattedData(formattedData, top_n, sector_ids);
        } catch (error) {
            console.error(`❌ Error fetching top losers by price segment: ${error.message}`);
            alert(`Failed to fetch Top Losers by Price Segment: ${error.message}`);
            return null;
        }
    }

    // ===========================================
    // TOP VOLUME METHODS
    // ===========================================

    async fetchTopVolumeByShares({ date = null, top_n = Infinity, dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('tvol_overall', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.tvol.overall, dataInterval, cacheKey);

            const formattedData = await this.formatVolumeData(rawData, top_n);
            return formattedData;
        } catch (error) {
            console.error(`❌ Error fetching top volume by shares: ${error.message}`);
            alert(`Failed to fetch Top Volume by Shares: ${error.message}`);
            return null;
        }
    }

    async fetchTopVolumeBySharesSegment({ date = null, top_n = 6, sector_ids = [], dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('tvol_segment', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.tvol.segment, dataInterval, cacheKey);

            const formattedData = await this.formatVolumeSectorData(rawData, Infinity, []);
            const slicedData = this.sliceFormattedData(formattedData, top_n, sector_ids);
            console.log('slicedData-vol', slicedData)
            return slicedData
        } catch (error) {
            console.error(`❌ Error fetching top volume by shares segment: ${error.message}`);
            alert(`Failed to fetch Top Volume by Shares Segment: ${error.message}`);
            return null;
        }
    }
    // ===========================================
    // SHORT INTEREST METHODS
    // ===========================================

    async fetchShortInterestBySegment({ date = null, top_n = 6, sector_ids = [], dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('sip_segment', dataInterval);

            const rawData = await this.fetchFromEndpoint(this.endpoints.sip.segment, '1d', cacheKey);
            // console.info(`Fetched top dollar volume by segment: `, JSON.stringify(rawData, null, 2));

            const formattedData = await this.formatShortInterestData(rawData, Infinity, [], true);
            // console.info(`Formatted top dollar volume by segment: `, JSON.stringify(formattedData, null, 2));

            const slicedData = this.sliceFormattedData(formattedData, top_n, sector_ids);
            console.log('slicedData-sip', slicedData)
            return slicedData;
        } catch (error) {
            console.error(`❌ Error fetching Short Interest by segment: ${error.message}`);
            alert(`Failed to fetch Short Interest by Segment: ${error.message}`);
            return null;
        }
    }

    async fetchShortInterestOverall({ date = null, top_n = 49, dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('sip_overall', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.sip.overall, '1d', cacheKey);

            const formattedData = await this.formatShortInterestData(rawData, Infinity);
            const slicedData = this.sliceFormattedData(formattedData, top_n, []);
            console.log('sip-dData', slicedData)
            return slicedData;
        } catch (error) {
            console.error(`❌ Error fetching Short Interest overall: ${error.message}`);
            alert(`Failed to fetch Short Interest Overall: ${error.message}`);
            return null;
        }
    }

    // ===========================================
    // DIVIDEND METHODS
    // ===========================================

    async fetchDividendBySegment({ date = null, top_n = 6, sector_ids = [], dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('dividend_segment', dataInterval);

            const rawData = await this.fetchFromEndpoint(this.endpoints.dividend.segment, '1d', cacheKey);

            const formattedData = await this.formatDividendData(rawData, Infinity, [], true);
            const slicedData = this.sliceFormattedData(formattedData, top_n, sector_ids);
            console.log('slicedData-dividend', slicedData)
            return slicedData;
        } catch (error) {
            console.error(`❌ Error fetching Dividend by segment: ${error.message}`);
            alert(`Failed to fetch Dividend by Segment: ${error.message}`);
            return null;
        }
    }

    async fetchDividendOverall({ date = null, top_n = 49, dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('dividend_overall', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.dividend.overall, '1d', cacheKey);

            const formattedData = await this.formatDividendData(rawData, Infinity);
            const slicedData = this.sliceFormattedData(formattedData, top_n, []);
            console.log('dividend-data', slicedData)
            return slicedData;
        } catch (error) {
            console.error(`❌ Error fetching Dividend overall: ${error.message}`);
            alert(`Failed to fetch Dividend Overall: ${error.message}`);
            return null;
        }
    }
    // ===========================================
    // MARKET MAP METHODS
    // ===========================================
    async fetchMarketMap({ date = null, top_n = 6, sector_ids = [], dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('mmap', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.mmap.segment, dataInterval, cacheKey);

            // Count companies from API
            const apiCount = rawData?.data ? Object.values(rawData.data).flat().length : 0;
            console.log(`[count mmap] Скільки прилітає компаній з API: ${apiCount}`);

            const formattedData = await this.formatMarketMapData(rawData, Infinity, [], true);

            const slicedData = this.sliceFormattedData(formattedData, top_n, sector_ids);



            if (rawData?.ticker_info && slicedData.stockInfo) {
                for (const [ticker, info] of Object.entries(rawData.ticker_info)) {
                    if (slicedData.stockInfo[ticker]) {
                        slicedData.stockInfo[ticker] = {
                            ...slicedData.stockInfo[ticker],
                            ...info
                        };
                    }
                }
            }

            console.log('mmap-data', slicedData);

            return slicedData;
        } catch (error) {
            console.error(`❌ Error fetching Market Map: ${error.message}`);
            alert(`Failed to fetch Market Map: ${error.message}`);
            return null;
        }
    }


    // ===========================================
    // MY WATCHLIST METHODS
    // ===========================================

    async fetchMyWatchlist({ date = null, top_n = 49, dataInterval = '1d' } = {}) {
        try {
            const cacheKey = this.generateCacheKey('mwl', dataInterval);
            const rawData = await this.fetchFromEndpoint(this.endpoints.mwl.base, dataInterval, cacheKey);

            const formattedData = await this.formatMyPortfolioPricesData(rawData, top_n, []);
            return formattedData;
        } catch (error) {
            console.error(`❌ Error fetching my watchlist: ${error.message}`);
            alert(`Failed to fetch My Watchlist: ${error.message}`);
            return null;
        }
    }

    // ===========================================
    // ABNORMAL VOLUME METHODS
    // ===========================================

    async fetchAbnormalVolume({ date = null, top_n = Infinity } = {}) {
        try {
            const cacheKey = this.generateCacheKey('adv', '1d');
            const rawData = await this.fetchFromEndpoint(this.endpoints.adv.base, '1d', cacheKey);

            const formattedData = await this.formatAbnormalVolumeData(rawData, top_n);
            return formattedData;
        } catch (error) {
            console.error(`❌ Error fetching abnormal volume: ${error.message}`);
            alert(`Failed to fetch Abnormal Volume: ${error.message}`);
            return null;
        }
    }

    // ===========================================
    // DATA FORMATTING METHODS
    // ===========================================
    /**
     * FormatMarketMap Data (V5)
     */
    async formatMarketMapData(rawData, topN = Infinity, sectorIds = []) {
        const intervals = {};
        const headers = rawData.headers || [];
        const data = rawData.data || {};
        const ticker_info = rawData.ticker_info || {};

        const mapRecord = (record) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = record[index];
            });
            if (obj.ticker) obj.ticker = obj.ticker.replace(/[\x00-\x1F\x7F\u200B\uFEFF]/g, '').trim();
            return obj;
        };


        for (const timestamp in data) {
            if (Array.isArray(data[timestamp])) {
                const records = data[timestamp].map(mapRecord);

                const bySector = {};
                records.forEach(stock => {
                    const sector = stock.sector_id || 'unknown';
                    if (!bySector[sector]) bySector[sector] = [];
                    bySector[sector].push(stock);
                });

                intervals[timestamp] = [];
                const targetSectors = sectorIds.length > 0 ? sectorIds : Object.keys(bySector);
                for (const sector of targetSectors) {
                    const sectorStocks = bySector[sector] || [];
                    const formattedStocks = getFormattedStocks(sectorStocks, timestamp)
                    intervals[timestamp] = intervals[timestamp].concat(formattedStocks);
                }
                console.log(`Formatted ${timestamp} with ${intervals[timestamp].length} stocks`);
            }
        }


        const timestamps = Object.keys(intervals).sort();
        const latestTimestamp = timestamps[timestamps.length - 1] || new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-');

        const stockInfo = intervals[latestTimestamp]
            ? await this.enrichWithTickerSummary(intervals[latestTimestamp], rawData.ticker_info || {})
            : [];

        const stocksDict = this.convertToIndexedDictionary(stockInfo);

        return {
            intervals,
            stockInfo: stocksDict,
            metadata: {
                date: rawData.date,
                count: Object.values(data).flat().length,
                headers: rawData.headers,
                previous_close_date: Object.values(ticker_info)[0]?.previous_close_date || ''
            }
        };


        function getFormattedStocks(rawData, timestamp) {
            const formattedStocks = rawData
                .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                .slice(0, topN)
                .map((stock, index) => ({
                    ticker: stock.ticker,
                    todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                    dollarVolume: stock.cumulative_dollar_volume,
                    price: stock.price,
                    volume: stock.volume,
                    cumulative_volume: stock.cumulative_volume,
                    timestamp: timestamp,
                    ranking: stock.ranking || (index + 1),
                    sector_id: stock.sector_id,
                    relative_volume_percentage: stock.relative_volume_percentage || 0,
                    avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                    daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0,
                    market_cap: stock.market_cap || 0,
                }));
            return formattedStocks
        }
    }

    /**
     * Format Short Interest Data (V5)
     */
    async formatShortInterestData(rawData, topN = Infinity, sectorIds = [], isSegment = false) {
        const intervals = {};
        const headers = rawData.headers || [];
        const data = rawData.data || {};
        const ticker_info = rawData.ticker_info || {};

        const mapRecord = (record) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = record[index];
            });
            if (obj.ticker) obj.ticker = obj.ticker.replace(/[\x00-\x1F\x7F\u200B\uFEFF]/g, '').trim();
            return obj;
        };


        for (const timestamp in data) {
            if (Array.isArray(data[timestamp])) {
                const records = data[timestamp].map(mapRecord);

                if (isSegment) {
                    const bySector = {};
                    records.forEach(stock => {
                        const sector = stock.sector_id || 'unknown';
                        if (!bySector[sector]) bySector[sector] = [];
                        bySector[sector].push(stock);
                    });

                    intervals[timestamp] = [];
                    const targetSectors = sectorIds.length > 0 ? sectorIds : Object.keys(bySector);
                    for (const sector of targetSectors) {
                        const sectorStocks = bySector[sector] || [];
                        const formattedStocks = getFormattedStocks(sectorStocks, timestamp);
                        intervals[timestamp] = intervals[timestamp].concat(formattedStocks);
                    }
                    console.log(`Formatted ${timestamp} with ${intervals[timestamp].length} stocks`);
                } else {
                    intervals[timestamp] = getFormattedStocks(records, timestamp);
                }

            }
        }


        const timestamps = Object.keys(intervals).sort();
        const latestTimestamp = timestamps[timestamps.length - 1] || new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-');

        const stockInfo = intervals[latestTimestamp]
            ? await this.enrichWithTickerSummary(intervals[latestTimestamp], rawData.ticker_info || {})
            : [];

        const stocksDict = this.convertToIndexedDictionary(stockInfo);

        return {
            intervals,
            stockInfo: stocksDict,
            metadata: {
                date: rawData.date,
                count: Object.values(data).flat().length,
                headers: rawData.headers
            }
        };


        function getFormattedStocks(rawData, timestamp) {
            const formattedStocks = rawData
                .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                .slice(0, topN)
                .map((stock, index) => ({
                    ticker: stock.ticker,
                    todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                    dollarVolume: stock.cumulative_dollar_volume,
                    price: stock.price,
                    volume: stock.volume,
                    cumulative_volume: stock.cumulative_volume,
                    timestamp: timestamp,
                    ranking: stock.ranking || (index + 1),
                    sector_id: stock.sector_id,
                    relative_volume_percentage: stock.relative_volume_percentage || 0,
                    avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                    daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0,
                    short_interest_pct: stock.short_interest_pct || 0,
                    market_cap: stock.market_cap || 0,
                }));

            return formattedStocks
        }
    }

    /**
     * Format Short Interest Data (V5)
     */
    async formatDividendData(rawData, topN = Infinity, sectorIds = [], isSegment = false) {
        const intervals = {};
        const headers = rawData.headers || [];
        const data = rawData.data || {};
        const ticker_info = rawData.ticker_info || {};

        const mapRecord = (record) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = record[index];
            });
            if (obj.ticker) obj.ticker = obj.ticker.replace(/[\x00-\x1F\x7F\u200B\uFEFF]/g, '').trim();
            return obj;
        };


        for (const timestamp in data) {
            if (Array.isArray(data[timestamp])) {
                const records = data[timestamp].map(mapRecord);

                if (isSegment) {
                    const bySector = {};
                    records.forEach(stock => {
                        const sector = stock.sector_id || 'unknown';
                        if (!bySector[sector]) bySector[sector] = [];
                        bySector[sector].push(stock);
                    });

                    intervals[timestamp] = [];
                    const targetSectors = sectorIds.length > 0 ? sectorIds : Object.keys(bySector);
                    for (const sector of targetSectors) {
                        const sectorStocks = bySector[sector] || [];
                        const formattedStocks = getFormattedStocks(sectorStocks, timestamp);
                        intervals[timestamp] = intervals[timestamp].concat(formattedStocks);
                    }
                    console.log(`Formatted ${timestamp} with ${intervals[timestamp].length} stocks`);
                } else {
                    intervals[timestamp] = getFormattedStocks(records, timestamp);
                }

            }
        }


        const timestamps = Object.keys(intervals).sort();
        const latestTimestamp = timestamps[timestamps.length - 1] || new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-');

        const stockInfo = intervals[latestTimestamp]
            ? await this.enrichWithTickerSummary(intervals[latestTimestamp], rawData.ticker_info || {})
            : [];

        const stocksDict = this.convertToIndexedDictionary(stockInfo);

        return {
            intervals,
            stockInfo: stocksDict,
            metadata: {
                date: rawData.date,
                count: Object.values(data).flat().length,
                headers: rawData.headers
            }
        };


        function getFormattedStocks(rawData, timestamp) {
            const formattedStocks = rawData
                .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                .slice(0, topN)
                .map((stock, index) => ({
                    ticker: stock.ticker,
                    todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                    dollarVolume: stock.cumulative_dollar_volume,
                    price: stock.price,
                    volume: stock.volume,
                    cumulative_volume: stock.cumulative_volume,
                    timestamp: timestamp,
                    ranking: stock.ranking || (index + 1),
                    sector_id: stock.sector_id,
                    relative_volume_percentage: stock.relative_volume_percentage || 0,
                    avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                    daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0,
                    trail_1y_div_yield_pct: stock.trail_1y_div_yield_pct || 0,
                    trail_1y_div: stock.trail_1y_div || 0,
                    market_cap: stock.market_cap || 0,
                }));

            return formattedStocks
        }
    }



    /**
     * Format Top Dollar Volume Data (V5)
     */
    async formatDollarVolumeData(rawData, topN = Infinity) {
        const intervals = {};
        const headers = rawData.headers || [];
        const data = rawData.data || {};
        const ticker_info = rawData.ticker_info || {};

        console.log('Processing dollar volume data:', { headers, data, topN, ticker_info });

        const mapRecord = (record) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = record[index];
            });
            if (obj.ticker) obj.ticker = obj.ticker.replace(/[\x00-\x1F\x7F\u200B\uFEFF]/g, '').trim();
            return obj;
        };

        for (const timestamp in data) {
            if (Array.isArray(data[timestamp])) {
                const records = data[timestamp].map(mapRecord);

                const formattedStocks = records
                    .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                    .slice(0, topN)
                    .map((stock, index) => ({
                        ticker: stock.ticker,
                        todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                        dollarVolume: stock.cumulative_dollar_volume,
                        price: stock.price,
                        volume: stock.volume,
                        cumulative_volume: stock.cumulative_volume,
                        timestamp: timestamp,
                        ranking: stock.ranking || (index + 1),
                        sector_id: stock.sector_id,
                        relative_volume_percentage: stock.relative_volume_percentage || 0,
                        avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                        daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0
                    }));

                intervals[timestamp] = formattedStocks;
                console.log(`Formatted ${timestamp} with ${intervals[timestamp].length} stocks`);
            }
        }

        const timestamps = Object.keys(intervals).sort();
        const latestTimestamp = timestamps[timestamps.length - 1] || new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-');

        const stockInfo = intervals[latestTimestamp]
            ? await this.enrichWithTickerSummary(intervals[latestTimestamp], rawData.ticker_info || {})
            : [];

        const stocksDict = this.convertToIndexedDictionary(stockInfo);

        return {
            intervals,
            stockInfo: stocksDict,
            metadata: {
                date: rawData.date,
                count: Object.values(data).flat().length,
                headers: rawData.headers,
                previous_close_date: Object.values(ticker_info)[0]?.previous_close_date || ''
            }
        };
    }

    /**
     * Format Top Dollar Volume Data (V5)
     */
    async formatDollarVolumeSectorData(rawData, topN = Infinity, sectorIds = []) {
        const intervals = {};
        const headers = rawData.headers || [];
        const data = rawData.data || {};
        const ticker_info = rawData.ticker_info || {};

        console.log('Processing dollar volume data:', { headers, data, topN, sectorIds, ticker_info });

        const mapRecord = (record) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = record[index];
            });
            if (obj.ticker) obj.ticker = obj.ticker.replace(/[\x00-\x1F\x7F\u200B\uFEFF]/g, '').trim();
            return obj;
        };

        for (const timestamp in data) {
            if (Array.isArray(data[timestamp])) {
                const records = data[timestamp].map(mapRecord);

                const bySector = {};
                records.forEach(stock => {
                    const sector = stock.sector_id || 'unknown';
                    if (!bySector[sector]) bySector[sector] = [];
                    bySector[sector].push(stock);
                });

                intervals[timestamp] = [];
                const targetSectors = sectorIds.length > 0 ? sectorIds : Object.keys(bySector);
                for (const sector of targetSectors) {
                    const sectorStocks = bySector[sector] || [];
                    // console.log(`Sector ${sector} has ${sectorStocks.length} stocks`);

                    const formattedStocks = sectorStocks
                        .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                        .slice(0, topN)
                        .map((stock, index) => ({
                            ticker: stock.ticker,
                            todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                            dollarVolume: stock.cumulative_dollar_volume,
                            price: stock.price,
                            volume: stock.volume,
                            cumulative_volume: stock.cumulative_volume,
                            timestamp: timestamp,
                            ranking: stock.ranking || (index + 1),
                            sector_id: stock.sector_id,
                            relative_volume_percentage: stock.relative_volume_percentage || 0,
                            avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                            daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0

                        }));

                    intervals[timestamp] = intervals[timestamp].concat(formattedStocks);
                }
                console.log(`Formatted ${timestamp} with ${intervals[timestamp].length} stocks`);
            }
        }

        const timestamps = Object.keys(intervals).sort();
        const latestTimestamp = timestamps[timestamps.length - 1] || new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-');

        const stockInfo = intervals[latestTimestamp]
            ? await this.enrichWithTickerSummary(intervals[latestTimestamp], rawData.ticker_info || {})
            : [];
        // console.log('Enriched dollar volume stocks:', stockInfo);

        const stocksDict = this.convertToIndexedDictionary(stockInfo);
        // console.log('Enriched dollar volume stocks after convertToIndexedDictionary:', stocksDict);

        return {
            intervals,
            stockInfo: stocksDict,
            metadata: {
                date: rawData.date,
                count: Object.values(data).flat().length,
                headers: rawData.headers,
                previous_close_date: Object.values(ticker_info)[0]?.previous_close_date || ''
            }
        };
    }

    async formatMyPortfolioPricesData(rawData, topN = Infinity, sortBy = 'first') {
        const intervals = {};
        const headers = rawData.headers || [];
        const data = rawData.data || {};

        console.log('Processing my portfolio prices data:', { headers, data, topN, sortBy });

        const mapRecord = (record) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = record[index];
            });
            if (obj.ticker) obj.ticker = obj.ticker.replace(/[\x00-\x1F\x7F\u200B\uFEFF]/g, '').trim();
            return obj;
        };

        // Step 1: Format all records without sorting
        for (const timestamp in data) {
            if (Array.isArray(data[timestamp])) {
                const records = data[timestamp].map(mapRecord);
                intervals[timestamp] = records.map((stock) => ({
                    ranking: stock.ranking || (index + 1),
                    ticker: stock.ticker,
                    todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                    dollarVolume: stock.cumulative_dollar_volume,
                    price: stock.price,
                    volume: stock.volume,
                    timestamp: timestamp,
                    relative_volume_percentage: stock.relative_volume_percentage || 0,
                    avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                    daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0
                }));
                console.log(`Formatted ${timestamp} with ${intervals[timestamp].length} stocks for prices`);
            }
        }

        const timestamps = Object.keys(intervals).sort();
        if (!timestamps.length) {
            console.log('No timestamps available, returning empty result');
            return {
                intervals: {},
                stockInfo: {},
                metadata: {
                    date: rawData.date,
                    count: 0,
                    headers: rawData.headers
                }
            };
        }

        // Step 2: Determine the sorting timestamp
        const sortTimestamp = sortBy === 'last' ? timestamps[timestamps.length - 1] : timestamps[0];
        console.log(`Selected sortTimestamp: ${sortTimestamp}`);

        // Step 3: Get the sorted order from the selected timestamp
        const sortedStocks = intervals[sortTimestamp]
            ? [...intervals[sortTimestamp]]
                .sort((a, b) => (a.todaysChangePerc || -Infinity) - (b.todaysChangePerc || -Infinity)) // Ascending order
                .slice(0, topN)
            : [];
        const sortedTickers = sortedStocks.map(stock => stock.ticker);
        console.log('sortedTickers:', sortedTickers);

        // Step 4: Rebuild each interval to match the sorted ticker order
        const orderedIntervals = {};
        for (const timestamp of timestamps) {
            const currentInterval = intervals[timestamp];
            const orderedInterval = [];
            for (const ticker of sortedTickers) {
                const stock = currentInterval.find(s => s.ticker === ticker);
                if (stock) {
                    orderedInterval.push(stock);
                } else {
                    orderedInterval.push({
                        ticker,
                        todaysChangePerc: 0,
                        dollarVolume: 0,
                        price: 0,
                        volume: 0,
                        timestamp,
                        relative_volume_percentage: 0,
                        avg_daily_volume_30d: 0
                    });
                }
            }
            orderedIntervals[timestamp] = orderedInterval;
            console.log(`Ordered interval ${timestamp} tickers:`, orderedInterval.map(s => s.ticker));
        }

        const latestTimestamp = timestamps[timestamps.length - 1] || new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-');

        const stockInfo = orderedIntervals[latestTimestamp]
            ? await this.enrichWithTickerSummary(orderedIntervals[latestTimestamp], rawData.ticker_info || {})
            : [];
        console.log('Enriched my portfolio prices stocks:', stockInfo);

        const stocksDict = this.convertToIndexedDictionary(stockInfo);
        console.log('Enriched my portfolio prices stocks after convertToIndexedDictionary:', stocksDict);

        return {
            intervals: orderedIntervals,
            stockInfo: stocksDict,
            metadata: {
                date: rawData.date,
                count: Object.values(data).flat().length,
                headers: rawData.headers
            }
        };
    }

    async formatTopMoversData(rawData, topN = Infinity, sectorIds = []) {
        const intervals = {};
        const headers = rawData.headers || [];
        const data = rawData.data || {};

        console.log('Processing top movers data:', { headers, data, topN, sectorIds });

        const mapRecord = (record) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = record[index];
            });
            if (obj.ticker) obj.ticker = obj.ticker.replace(/[\x00-\x1F\x7F\u200B\uFEFF]/g, '').trim();
            return obj;
        };

        for (const timestamp in data) {
            if (Array.isArray(data[timestamp])) {
                const records = data[timestamp].map(mapRecord);
                console.log(`Total records for ${timestamp}: ${records.length}`);

                const bySector = {};
                records.forEach(stock => {
                    const sector = stock.sector_id || 'unknown';
                    if (!bySector[sector]) bySector[sector] = [];
                    bySector[sector].push(stock);
                });

                intervals[timestamp] = [];
                const targetSectors = sectorIds.length > 0 ? sectorIds : Object.keys(bySector);
                for (const sector of targetSectors) {
                    const sectorStocks = bySector[sector] || [];
                    // console.log(`Sector ${sector} has ${sectorStocks.length} stocks`);

                    const allGainers = sectorStocks.filter(stock => stock.performance_type === "pgain");
                    const allLosers = sectorStocks.filter(stock => stock.performance_type === "plose");
                    // console.log(`Sector ${sector} - All Gainers: ${allGainers.length}, All Losers: ${allLosers.length}`);

                    const gainers = allGainers
                        .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                        .slice(0, topN)
                        .map((stock, index) => ({
                            ticker: stock.ticker,
                            todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                            volume: stock.volume,
                            price: stock.price,
                            timestamp: timestamp,
                            ranking: stock.ranking || (index + 1),
                            sector_id: stock.sector_id,
                            perf: 'G',
                            avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                            relative_volume_percentage: stock.relative_volume_percentage || 0,
                            daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0
                        }));

                    const losers = allLosers
                        .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                        .slice(0, topN)
                        .map((stock, index) => ({
                            ticker: stock.ticker,
                            todaysChangePerc: Number(stock.price_pct_change_vs_prev_close?.toFixed(2) || 0),
                            volume: stock.volume,
                            price: stock.price,
                            timestamp: timestamp,
                            ranking: stock.ranking || (index + 1),
                            sector_id: stock.sector_id,
                            perf: 'L',
                            avg_daily_volume_30d: stock.avg_daily_volume_30d || 0,
                            relative_volume_percentage: stock.relative_volume_percentage || 0,
                            daypart_relative_volume_percentage: stock.daypart_relative_volume_percentage || 0
                        }));

                    intervals[timestamp] = intervals[timestamp].concat(gainers, losers);
                    // console.log(`Sector ${sector} - Formatted Gainers: ${gainers.length}, Losers: ${losers.length}`);
                }
                // console.log(`Formatted ${timestamp} with ${intervals[timestamp].length} stocks`);
            }
        }

        const timestamps = Object.keys(intervals).sort();
        const latestTimestamp = timestamps[timestamps.length - 1] || new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-');

        const stockInfo = intervals[latestTimestamp]
            ? await this.enrichWithTickerSummary(intervals[latestTimestamp], rawData.ticker_info || {})
            : [];
        // console.log('Enriched top movers stocks:', stockInfo);

        const stocksDict = this.convertToIndexedDictionary(stockInfo);
        // console.log('Enriched top mover stocks after convertToIndexedDictionary:', stocksDict);
        return {
            intervals,
            stockInfo: stocksDict,
            metadata: {
                date: rawData.date,
                count: Object.values(data).flat().length,
                headers: rawData.headers,
                previous_close_date: rawData.ticker_info ? (Object.values(rawData.ticker_info)[0]?.previous_close_date || '') : ''
            }
        };
    }

    /**
     * Format Volume Data (V5)
     */
    async formatVolumeData(rawData, topN = Infinity) {
        console.log('📊 Formatting V5 Volume data...', { topN });

        // Use the same logic as formatDollarVolumeData
        const result = await this.formatDollarVolumeData(rawData, topN);
        result.metadata.dataType = 'volume';
        return result;
    }

    /**
     * Format Volume Data (V5)
     */
    async formatVolumeSectorData(rawData, topN = Infinity, sectorIds = []) {
        console.log('📊 Formatting V5 Volume data...', { topN, sectorIds });

        // Use the same logic as formatDollarVolumeSectorData
        const result = await this.formatDollarVolumeSectorData(rawData, topN, sectorIds);
        result.metadata.dataType = 'volume';
        return result;
    }

    /**
     * Format Abnormal Volume Data (V5)
     */
    async formatAbnormalVolumeData(rawData, topN = Infinity) {
        console.log('📊 Formatting V5 Abnormal Volume data...', { topN });

        // Use the same logic as formatDollarVolumeData
        const result = await this.formatDollarVolumeData(rawData, topN);
        result.metadata.dataType = 'abnormalVolume';
        return result;
    }

    // ===========================================
    // UTILITY METHODS (From V3)
    // ===========================================

    /**
     * Slice formatted data by topN and sectorIds
     */
    sliceFormattedData(formattedData, topN, sectorIds, type = 'sectors') {
        console.log(`sliceFormattedData ${sectorIds} stocks for type ${type}`);

        const slicedIntervals = {};
        // const halfN = Math.floor(topN / 2); // Used when had 1/2 gainers/losers in same dataset
        const halfN = topN === -1? formattedData.stockInfo.length: topN;

        const enrichedByTicker = new Map();
        if (formattedData && formattedData.stockInfo) {
            Object.values(formattedData.stockInfo).forEach((s) => {
                if (s && s.ticker) {
                    enrichedByTicker.set(s.ticker, s);
                }
            });
        }

        for (const timestamp in formattedData.intervals) {
            const stocks = formattedData.intervals[timestamp];
            let slicedStocks;
            console.log(`sliceFormattedData timestamp loop, type ${type}`);
            if (type === 'prices') {
                // For prices, just slice as data has already been sorted.
                slicedStocks = stocks.slice(0, topN);
                // console.debug(`Sliced ${slicedStocks.length} stocks for timestamp ${timestamp} by todaysChangePerc`);
            } else {
                // For sectors, group by sector_id
                const bySector = {};
                stocks.forEach(stock => {
                    const sector = stock.sector_id || 'unknown';
                    if (!bySector[sector]) bySector[sector] = [];
                    bySector[sector].push(stock);
                });

                slicedIntervals[timestamp] = [];
                const targetSectors = sectorIds.length > 0 ? sectorIds : Object.keys(bySector);
                for (const sector of targetSectors) {
                    const sectorStocks = bySector[sector] || [];
                    let sectorSlicedStocks;

                    if (formattedData.metadata.headers.includes('perf')) {
                        const gainers = sectorStocks
                            .filter(stock => stock.perf === 'G')
                            .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                            .slice(0, halfN);
                        const losers = sectorStocks
                            .filter(stock => stock.perf === 'L')
                            .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                            .slice(0, halfN);
                        sectorSlicedStocks = [...gainers, ...losers];
                    } else {
                        sectorSlicedStocks = sectorStocks
                            .sort((a, b) => (a.ranking || Infinity) - (b.ranking || Infinity))
                            .slice(0, topN);
                    }

                    slicedIntervals[timestamp] = slicedIntervals[timestamp].concat(sectorSlicedStocks);
                }
                // console.debug(`Sliced ${slicedIntervals[timestamp].length} stocks for timestamp ${timestamp} across sectors ${targetSectors}`);
                continue;
            }

            slicedIntervals[timestamp] = slicedStocks;
        }

        const timestamps = Object.keys(slicedIntervals).sort();
        const latestTimestamp = timestamps[timestamps.length - 1] || null;
        const slicedStocks = slicedIntervals[latestTimestamp] || [];

        const slicedStockInfo = slicedStocks.reduce((dict, stock) => {
            const enrichedStock = enrichedByTicker.get(stock.ticker) || {};
            dict[stock.ticker] = {
                ...stock,
                tickerSummary: enrichedStock.tickerSummary || {}
                // tickerDetails omitted as it will be enriched on-demand in stock-bar.js
            };
            return dict;
        }, {});
        console.log('Sliced stockInfo with enrichment:', slicedStockInfo);
        // console.log('Sliced slicedIntervals with enrichment:', slicedIntervals);

        const metadata = {
            ...(formattedData.metadata || {})
        };

        if (!metadata.previous_close_date) {
            const firstWithSummary = Object.values(slicedStockInfo).find(
                s => s && s.tickerSummary && s.tickerSummary.previous_close_date
            );
            if (firstWithSummary) {
                metadata.previous_close_date = firstWithSummary.tickerSummary.previous_close_date;
            }
        }

        return {
            intervals: slicedIntervals,
            stockInfo: slicedStockInfo,
            metadata
        };
    }

    /**
     * Enrich stock data with ticker summary from Polygon API
     */
    async enrichWithTickerSummary(stocks, tickerInfo) {
        if (!stocks.length || !tickerInfo) return stocks;
        // console.log('Enriching stocks count:', stocks.length);
        // console.log('Enriching stocks ticker info count:', tickerInfo);

        const enrichedStocks = stocks.map(stock => {
            // console.log('Enriching stock:', stock);
            const cacheKey = this.generateTickerCacheKey(stock.ticker);
            let tickerSummary = null;

            // Check localStorage using storageManagement.js
            tickerSummary = getCacheItem(cacheKey);
            if (tickerSummary) {
                // console.log(`✅ Retrieved ticker summary for ${stock.ticker} from localStorage`);
            }

            // Use tickerInfo if not in cache or expired
            if (!tickerSummary && tickerInfo[stock.ticker]) {
                tickerSummary = {
                    name: tickerInfo[stock.ticker].name || stock.ticker,
                    primary_exchange: tickerInfo[stock.ticker].primary_exchange || 'Unknown Exchange',
                    market_cap: tickerInfo[stock.ticker].market_cap || 0,
                    previous_close: tickerInfo[stock.ticker].previous_close || 0,
                    previous_close_date: tickerInfo[stock.ticker].previous_close_date || ''
                };

                // Cache in localStorage using storageManagement.js
                setCacheItem(cacheKey, tickerSummary, this.tickerCacheExpiration);
                console.log(`✅ Cached ticker summary for ${stock.ticker} in localStorage`);
            }

            if (tickerSummary && !tickerSummary.previous_close_date && tickerInfo[stock.ticker]) {
                tickerSummary.previous_close_date = tickerInfo[stock.ticker].previous_close_date || '';
                try {
                    setCacheItem(cacheKey, tickerSummary, this.tickerCacheExpiration);
                } catch (_) { }
            }

            // Fallback if no tickerInfo
            if (!tickerSummary) {
                tickerSummary = {
                    name: stock.ticker,
                    primary_exchange: 'Unknown Exchange',
                    market_cap: 0,
                    previous_close: 0,
                    previous_close_date: ''
                };
                setCacheItem(cacheKey, tickerSummary, this.tickerCacheExpiration);
                console.log(`✅ Cached fallback ticker summary for ${stock.ticker} in localStorage`);
            }

            return {
                ...stock,
                tickerSummary
            };
        });

        console.log(`✅ Enriched ${enrichedStocks.length} stocks with ticker summary`);
        return enrichedStocks;
    }

    getExchangeForTicker(ticker, stockInfo) {
        // Check in-memory stockInfo first
        if (stockInfo && stockInfo[ticker] && stockInfo[ticker].tickerSummary) {
            return stockInfo[ticker].tickerSummary.primary_exchange || null;
        }

        // Check localStorage using storageManagement.js
        const cacheKey = this.generateTickerCacheKey(ticker);
        const tickerSummary = getCacheItem(cacheKey);
        return tickerSummary?.primary_exchange || null;
    }

    /**
     * Convert array of stocks to indexed dictionary
     */
    convertToIndexedDictionary(stocks) {
        const dictionary = {};

        stocks.forEach((stock, index) => {
            dictionary[index] = stock;
        });

        return dictionary;
    }
}

// Export for use
window.PVTechAPIManager = PVTechAPIManager;
